import { authTables } from "@convex-dev/auth/server";
import { defineSchema, defineTable } from "convex/server";
import { v } from "convex/values";

// ============================================
// ENUMS - TYPE D'ÉVALUATION
// ============================================

export enum TypeEvalEnum {
  INBASKET = "inbasket",
  FORM = "form",
  ENT_STRUCT = "ent-struct",
}

export const typeEvalValidator = v.union(
  v.literal(TypeEvalEnum.INBASKET),
  v.literal(TypeEvalEnum.FORM),
  v.literal(TypeEvalEnum.ENT_STRUCT),
);

// ============================================
// ENUMS - STATUT ÉVALUATION (global + analyse fusionnés)
// ============================================

export enum StatutGlobalEnum {
  NON_COMMENCE = "non_commence",
  EN_COURS = "en_cours",
  SOUMIS = "soumis",
  ANALYSE_EN_COURS = "analyse_en_cours",
  TERMINE = "termine",
  ERREUR_ANALYSE = "erreur_analyse",
}

export const statutGlobalValidator = v.union(
  v.literal(StatutGlobalEnum.NON_COMMENCE),
  v.literal(StatutGlobalEnum.EN_COURS),
  v.literal(StatutGlobalEnum.SOUMIS),
  v.literal(StatutGlobalEnum.ANALYSE_EN_COURS),
  v.literal(StatutGlobalEnum.TERMINE),
  v.literal(StatutGlobalEnum.ERREUR_ANALYSE),
);

/** Vrai si le candidat a soumis (évaluation non modifiable) */
export function estEvaluationSoumise(statut: StatutGlobalEnum): boolean {
  return (
    statut !== StatutGlobalEnum.NON_COMMENCE &&
    statut !== StatutGlobalEnum.EN_COURS
  );
}

// ============================================
// ENUMS - STATUT CAMPAGNE
// ============================================

export enum StatutCampagneEnum {
  BROUILLON = "brouillon",
  ACTIVE = "active",
  TERMINEE = "terminee",
}

export const statutCampagneValidator = v.union(
  v.literal(StatutCampagneEnum.BROUILLON),
  v.literal(StatutCampagneEnum.ACTIVE),
  v.literal(StatutCampagneEnum.TERMINEE),
);

// ============================================
// ENUMS - TEMPLATES FORM
// ============================================

export enum TemplateFormEnum {
  AUTODIAG = "autodiag",
  EGOGRAMME = "egogramme",
  PAMI = "pami",
  COPSOQ3 = "copsoq3",
}

export const templateFormValidator = v.union(
  v.literal(TemplateFormEnum.AUTODIAG),
  v.literal(TemplateFormEnum.EGOGRAMME),
  v.literal(TemplateFormEnum.PAMI),
  v.literal(TemplateFormEnum.COPSOQ3),
);

// ============================================
// ENUMS - TEMPLATES INBASKET
// ============================================

export enum TemplateInbasketEnum {
  DEFAULT = "default",
  RAPIDE = "rapide",
  // Futurs templates à ajouter ici
}

export const templateInbasketValidator = v.union(
  v.literal(TemplateInbasketEnum.DEFAULT),
  v.literal(TemplateInbasketEnum.RAPIDE),
);

// ============================================
// ENUMS - TEMPLATES ENT_STRUCT
// ============================================

export enum TemplateEntStructEnum {
  ASSISTANT_MANAGER_KFC_V1 = "assistant-manager-d-unite-marchande_v1",
  ASSISTANT_MANAGER_KFC_V2 = "assistant-manager-d-unite-marchande_v2",
  ASSISTANT_MANAGER_KFC_V3 = "assistant-manager-d-unite-marchande_v3",
  CHEF_CHANTIER_FERROVIAIRE_VF_LIGHT = "chef-chantier-ferroviaire_vf",
  CADRE_SANTE_V_COURTE = "cadre-de-sante-version-courte",
}

export const templateEntStructValidator = v.union(
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V1),
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V2),
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V3),
  v.literal(TemplateEntStructEnum.CHEF_CHANTIER_FERROVIAIRE_VF_LIGHT),
  v.literal(TemplateEntStructEnum.CADRE_SANTE_V_COURTE),
);

// ============================================
// ENUMS - STATUT DÉTAILLÉ INBASKET
// ============================================

export enum StatutDetailInbasketEnum {
  NON_COMMENCEE = "non_commencee",
  LECTURE_INFORMATIONS_CONTEXTE = "lecture_informations_contexte",
  LECTURE_MAILS = "lecture_mails",
  REPONSE_MAILS = "reponse_mails",
  PRIORISATION_MAILS = "priorisation_mails",
  MAIL_PRESIDENTE = "mail_presidente",
  FINIE = "finie",
  INCOMPLETE = "incomplete",
}

export const statutDetailInbasketValidator = v.union(
  v.literal(StatutDetailInbasketEnum.NON_COMMENCEE),
  v.literal(StatutDetailInbasketEnum.LECTURE_INFORMATIONS_CONTEXTE),
  v.literal(StatutDetailInbasketEnum.LECTURE_MAILS),
  v.literal(StatutDetailInbasketEnum.REPONSE_MAILS),
  v.literal(StatutDetailInbasketEnum.PRIORISATION_MAILS),
  v.literal(StatutDetailInbasketEnum.MAIL_PRESIDENTE),
  v.literal(StatutDetailInbasketEnum.FINIE),
  v.literal(StatutDetailInbasketEnum.INCOMPLETE),
);

// ============================================
// ENUMS - STATUT MAIL
// ============================================

export enum StatutMailEnum {
  NON_LU = "non_lu",
  LU = "lu",
  REPONDU = "repondu",
}

export const statutMailValidator = v.union(
  v.literal(StatutMailEnum.NON_LU),
  v.literal(StatutMailEnum.LU),
  v.literal(StatutMailEnum.REPONDU),
);

// ============================================
// ENUMS - STATUT DOCUMENT
// ============================================

export enum StatutDocumentEnum {
  NON_LU = "non_lu",
  LU = "lu",
}

export const statutDocumentValidator = v.union(
  v.literal(StatutDocumentEnum.NON_LU),
  v.literal(StatutDocumentEnum.LU),
);

// ============================================
// DONNÉES SPÉCIFIQUES PAR TYPE D'ÉVALUATION
// ============================================

// Sous-question autodiag (avec réponse)
export const formSousQuestionValidator = v.object({
  name: v.string(),
  label: v.string(),
  reponse: v.string(),
});

// Réponse autodiag (une compétence avec ses sous-questions)
export const formReponseAutodiagValidator = v.object({
  titre: v.optional(v.string()),
  name: v.string(),
  label: v.string(),
  reponse: v.string(),
  sous_questions: v.optional(v.array(formSousQuestionValidator)),
});

// Réponse egogramme (une question oui/non avec profil)
export const formReponseEgogrammeValidator = v.object({
  name: v.string(),
  label: v.string(),
  profil: v.string(),
  response: v.boolean(),
});

// Réponse COPSOQ3 (une question avec score brut)
export const formReponseCopsoq3Validator = v.object({
  name: v.string(),
  question: v.string(),
  echelle: v.string(),
  domaine: v.string(),
  reponse: v.string(),
  score: v.number(),
});

// Réponses Pami (une liste ordonnée d'items)
export const formReponsePamiValidator = v.object({
  name: v.string(),
  label: v.string(),
  orderedItems: v.array(
    v.object({
      id: v.string(),
      prefixLabel: v.string(),
      label: v.string(),
    }),
  ),
});
export const llmResultatValidator = v.object({
  jobId: v.string(),
  app: v.string(),
  timestamp: v.number(),
  data: v.any(),
});

export const questionsFinalesPamiValidator = v.object({
  langueMaternelle: v.boolean(),
  bonnesConditions: v.boolean(),
  reponsesSinceres: v.boolean(),
});

export const donneesFormValidator = v.object({
  type: v.literal(TypeEvalEnum.FORM),
  template: templateFormValidator,
  reponses: v.record(
    v.string(),
    v.union(
      formReponseAutodiagValidator,
      formReponseEgogrammeValidator,
      formReponseCopsoq3Validator,
      formReponsePamiValidator,
    ),
  ),
  questionsFinales: v.optional(questionsFinalesPamiValidator),
  resultats: v.optional(v.array(llmResultatValidator)),
});

export const donneesInbasketValidator = v.object({
  type: v.literal(TypeEvalEnum.INBASKET),
  template: v.optional(templateInbasketValidator),
  statutDetail: statutDetailInbasketValidator,
  mailSelectionneId: v.optional(v.string()),
  statutsDocuments: v.record(v.string(), statutDocumentValidator),
  reponses: v.record(
    v.string(),
    v.object({
      couleur: v.optional(v.string()),
      statut: statutMailValidator,
      transfertA: v.optional(v.string()),
      important: v.optional(v.boolean()),
      estEnBrouillon: v.optional(v.boolean()),
      annotations: v.optional(v.any()),
      reponse: v.optional(v.string()),
    }),
  ),
  resultats: v.optional(v.array(llmResultatValidator)),
});

// ============================================
// DONNÉES TEMPLATE ENTRETIEN STRUCTURÉ
// ============================================

export const entStructCritereValidator = v.object({
  criteriaId: v.string(),
  description: v.string(),
  points: v.number(),
  followup: v.string(),
});

export const entStructQuestionValidator = v.object({
  questionId: v.string(),
  type: v.string(),
  texte: v.string(),
  criteres: v.array(entStructCritereValidator),
});

export const entStructSousCompetenceValidator = v.object({
  nom: v.string(),
  poids: v.number(),
  questions: v.optional(v.array(entStructQuestionValidator)),
});

export const entStructCompetenceValidator = v.object({
  nom: v.string(),
  poids: v.number(),
  questions: v.optional(v.array(entStructQuestionValidator)),
  sousCompetences: v.optional(v.array(entStructSousCompetenceValidator)),
});

// État runtime d'un critère (tel que retourné par l'API chatbot)
export const entStructCriteriaEtatValidator = v.object({
  criteriaId: v.string(),
  description: v.string(),
  points: v.number(),
  covered: v.boolean(),
  asked: v.boolean(),
});

// État runtime d'une question (tel que retourné par l'API chatbot)
export const entStructQuestionEtatValidator = v.object({
  questionId: v.string(),
  competence: v.string(),
  competence_weight: v.optional(v.number()), // Poids de la compétence pour le calcul pondéré
  sous_competence: v.optional(v.union(v.string(), v.null())), // Nom de la sous-compétence (si applicable)
  sous_competence_weight: v.optional(v.union(v.number(), v.null())), // Poids de la sous-compétence pour le calcul pondéré
  question_text: v.string(),
  no_relance: v.optional(v.union(v.boolean(), v.null())),
  criteria: v.array(entStructCriteriaEtatValidator),
});

// Métadonnées de transcription pour un message utilisateur
export const entStructTranscriptionMetaValidator = v.object({
  caracteresTranscrits: v.number(), // Nombre de caractères venant de la transcription
  caracteresTotal: v.number(), // Nombre total de caractères du message
  pourcentage: v.number(), // Pourcentage (calculé pour faciliter les requêtes)
});

// Métadonnées de détection de triche pour un message utilisateur
export const entStructCheatingMetaValidator = v.object({
  // Données temporelles (en millisecondes)
  tempsAffichageQuestion: v.number(), // Temps entre affichage et premier caractère
  tempsRedaction: v.number(), // Temps total de rédaction
  tempsTotal: v.number(), // Temps entre question et envoi

  // Données de frappe
  vitesseFrappe: v.number(), // Caractères par seconde
  nombreEdits: v.number(), // Nombre de modifications
  pasteDetecte: v.boolean(), // Événement paste détecté

  // Données navigateur
  changementsOnglet: v.number(), // Nombre de tab switches
  tempsHorsPage: v.number(), // Temps total hors page (ms)
  absences: v.array(
    v.object({
      debut: v.number(), // Timestamp début absence
      fin: v.number(), // Timestamp fin absence
      duree: v.number(), // Durée en ms
    }),
  ),

  // Score calculé (0-100)
  scoreSuspicion: v.number(),
});

// Message d'entretien structuré (assistant ou candidat)
export const entStructMessageValidator = v.object({
  role: v.union(v.literal("assistant"), v.literal("user")),
  content: v.string(),
  // Contexte d'évaluation au moment du message (uniquement sur messages assistant)
  questionId: v.optional(v.string()),
  criteriaEtat: v.optional(v.array(entStructCriteriaEtatValidator)),
  // Métadonnées de transcription (uniquement sur messages utilisateur)
  transcriptionMeta: v.optional(entStructTranscriptionMetaValidator),
  // Métadonnées de détection de triche (uniquement sur messages utilisateur)
  cheatingMeta: v.optional(entStructCheatingMetaValidator),
  // Timestamps pour l'analyse de concentration (uniquement sur messages utilisateur)
  displayedAt: v.optional(v.number()),
  answeredAt: v.optional(v.number()),
  typewriterEndAt: v.optional(v.number()),
  firstResponseAt: v.optional(v.number()),
});

export const donneesEntStructValidator = v.object({
  type: v.literal(TypeEvalEnum.ENT_STRUCT),
  template: v.optional(templateEntStructValidator),
  threadId: v.optional(v.string()),
  reponses: v.record(v.string(), entStructMessageValidator),
  // État global des questions mis à jour à chaque échange
  questionsEtat: v.optional(v.array(entStructQuestionEtatValidator)),
  questionCouranteId: v.optional(v.string()),
  resultats: v.optional(v.array(llmResultatValidator)),
});

export const donneesEvaluationValidator = v.union(
  donneesInbasketValidator,
  donneesFormValidator,
  donneesEntStructValidator,
);

// ============================================
// SCHEMA
// ============================================

const schema = defineSchema({
  ...authTables,

  // ============================================
  // TABLE PRINCIPALE - ÉVALUATIONS
  // ============================================
  evaluations: defineTable({
    typeEval: typeEvalValidator,
    statut: statutGlobalValidator,
    template: v.optional(v.string()),
    dateDebut: v.optional(v.number()),
    dateFin: v.optional(v.number()),
    retoursExperience: v.optional(v.record(v.string(), v.string())),
    donnees: donneesEvaluationValidator,
    jobId: v.optional(v.string()),
    parcoursId: v.optional(v.id("parcoursEvaluations")),
  })
    .index("by_jobId", ["jobId"])
    .index("by_parcoursId", ["parcoursId"])
    .index("by_typeEval_statut", ["typeEval", "statut"]),

  // ============================================
  // TABLE - CAMPAGNES
  // ============================================
  campagnes: defineTable({
    nom: v.string(),
    typeParcours: v.string(),
    statut: statutCampagneValidator,
    dateDebut: v.optional(v.number()),
    dateFin: v.optional(v.number()),
    dateCreation: v.number(),
    dateModification: v.number(),
    evaluationsConfig: v.array(
      v.object({
        typeEval: typeEvalValidator,
        template: v.optional(v.string()),
      }),
    ),
    anonyme: v.optional(v.boolean()),
    progressionGlobale: v.optional(
      v.object({
        nbCandidats: v.number(),
        total: v.number(),
        terminees: v.number(),
        enCours: v.number(),
        nonCommencees: v.number(),
        pourcentage: v.number(),
      }),
    ),
  }),

  // ============================================
  // TABLE PRINCIPALE - PARCOURS D'ÉVALUATIONS
  // ============================================
  parcoursEvaluations: defineTable({
    emailCandidat: v.optional(v.string()),
    nomCandidat: v.optional(v.string()),
    prenomCandidat: v.optional(v.string()),
    typeParcours: v.string(),
    slug: v.string(),
    titre: v.string(),
    description: v.optional(v.string()),
    dateCreation: v.number(),
    dateModification: v.number(),
    evaluationsIds: v.array(v.id("evaluations")),
    archive: v.optional(v.boolean()),
    campagneId: v.optional(v.id("campagnes")),
    token: v.optional(v.string()),
    anonyme: v.optional(v.boolean()),
    evaluationsCache: v.optional(
      v.array(
        v.object({
          evalId: v.id("evaluations"),
          typeEval: typeEvalValidator,
          template: v.optional(v.string()),
          statut: statutGlobalValidator,
          dateDebut: v.optional(v.number()),
          dateFin: v.optional(v.number()),
        }),
      ),
    ),
    statutCache: v.optional(
      v.union(
        v.literal("non_commence"),
        v.literal("en_cours"),
        v.literal("termine"),
        v.literal("archive"),
      ),
    ),
    searchText: v.optional(v.string()),
  })
    .index("by_campagneId", ["campagneId"])
    .index("by_token", ["token"])
    .index("by_statutCache", ["statutCache"]),

  // ============================================
  // TABLE LIMITE DE TENTATIVES DE CONNEXION
  // ============================================
  tentativesLimiteConnexion: defineTable({
    cle: v.string(),
    tentatives: v.number(),
    premiereTentativeLe: v.number(),
    bloqueJusquA: v.optional(v.number()),
  }),
});

export default schema;
